import { App as AntdApp, Button, Dropdown, Tooltip } from "antd"
import { Download } from "lucide-react"
import { useQueryClient } from "@tanstack/react-query"
import type { TileResult } from "@csbi/contracts"
import { downloadCsv } from "@csbi/widgets"
import { useFilterStore } from "../../filters/model/filter-store.js"
import { buildTileRequest } from "../api.js"
import { createTileFingerprint } from "../tile-fingerprint.js"
import type { TileSpec } from "../hooks/use-tile-query.js"
import { toLongCsv, type ExportTile } from "../section-csv.js"

/**
 * ⚠ **It reads the query cache instead of fetching.** The data is already on the screen — refetching would both
 * cost a round of queries nobody asked for and, worse, could return *different numbers* from the ones the person
 * is looking at while they press the button. An export that disagrees with the screen is harder to notice than a
 * missing one, and impossible to explain afterwards.
 */

export type ExportGroup = {
  label: string
  tiles: readonly { title: string; spec: TileSpec }[]
}

export type ExportMenuProps = {
  fileBase: string
  groups: readonly ExportGroup[]
}

const safeName = (s: string) => s.replace(/[\\/:*?"<>|]/g, "-").trim()

export function ExportMenu({ fileBase, groups }: ExportMenuProps) {
  const { message } = AntdApp.useApp()
  const queryClient = useQueryClient()
  const filters = useFilterStore((s) => s.filters)

  const nonEmpty = groups.filter((g) => g.tiles.length > 0)

  const cached = (spec: TileSpec): TileResult | undefined =>
    queryClient.getQueryData<TileResult>(["tiles", spec.tileKey, createTileFingerprint(buildTileRequest(spec, filters))])

  const exportGroups = (label: string, groupsToExport: readonly ExportGroup[]) => {
    const wanted = groupsToExport.flatMap((g) => g.tiles)
    const ready: ExportTile[] = []
    for (const t of wanted) {
      const result = cached(t.spec)
      if (result && result.state === "ok" && result.rows.length > 0) ready.push({ title: t.title, result })
    }

    if (ready.length === 0) {
      void message.warning(
        "Nothing to export yet — none of these charts has data on screen. Wait for them to load, or widen the filters.",
        5,
      )
      return
    }

    downloadCsv(`${safeName(fileBase)} - ${safeName(label)}.csv`, toLongCsv(ready))

    const missing = wanted.length - ready.length
    if (missing > 0) {
      void message.info(
        `Exported ${ready.length} of ${wanted.length} charts. ${missing} had no data on screen and are not in the file.`,
        6,
      )
    }
  }

  if (nonEmpty.length === 0) return null

  const items = [
    ...(nonEmpty.length > 1
      ? [{ key: "__all__", label: "Everything on this page", onClick: () => exportGroups("All", nonEmpty) }]
      : []),
    ...nonEmpty.map((g) => ({ key: g.label, label: g.label, onClick: () => exportGroups(g.label, [g]) })),
  ]

  return (
    <Dropdown menu={{ items }} trigger={["click"]}>
      <Tooltip title="Export these charts as one CSV">
        <Button size="small" icon={<Download size={14} />}>
          Export
        </Button>
      </Tooltip>
    </Dropdown>
  )
}
